// Operator presence, console side (#812) — what the panel is allowed to claim about a person.
//
// The rule the whole file exists for: when the console cannot reach the presence service, every
// colleague becomes `unknown`, never `offline`. Offline is a statement about someone — that they
// are not at their desk — and a failed request is not evidence for it. Keeping the last known
// states would be the same mistake more slowly: they would go on looking current while ageing.
import type { PresenceMember, PresenceView } from "../api/types";

/** What the console may say about one person, including the word the server cannot send. */
export type DisplayPresenceState = "online" | "offline" | "unknown";

export interface PresenceRow {
  readonly userId: string;
  /** What a reader sees: the person's name, or their address when they have not set one. */
  readonly label: string;
  readonly email: string;
  readonly role: string;
  readonly state: DisplayPresenceState;
}

/**
 * The panel as a whole.
 *
 * `unavailable` and `empty` are different: one means the console could not ask, the other means
 * it asked and this business has one person in it. Collapsing them would tell a sole operator
 * their roster is broken.
 */
export type PresencePanelState = "loading" | "unavailable" | "empty" | "ready";

export interface PresenceModel {
  readonly state: PresencePanelState;
  readonly rows: readonly PresenceRow[];
  /** Null whenever the states are not trustworthy, so no count is ever shown over `unknown`s. */
  readonly onlineCount: number | null;
  readonly total: number;
}

export function memberLabel(member: Pick<PresenceMember, "name" | "email">): string {
  const name = (member.name ?? "").trim();
  return name.length > 0 ? name : member.email;
}

/**
 * Turn the last answer, plus whether the last request failed, into what to render.
 *
 * `view` is the most recent successful answer and may be from before the failure; `failed` says
 * the console cannot currently reach the service. When both are present the roster is still shown
 * — the people are real and did not stop existing — but every state degrades to `unknown`.
 */
export function presenceModel(input: {
  view: PresenceView | null;
  failed: boolean;
}): PresenceModel {
  // An answer that is not a roster is not an answer. A body with no `members` array — a proxy
  // error page, a truncated response, a future field rename — must degrade to "cannot say" like
  // any other failure, and must never reach the map below: this panel sits on the Overview, and
  // an exception here would take the whole screen down with it.
  const members = Array.isArray(input.view?.members) ? input.view.members : null;
  if (!members) {
    // Nothing yet and nothing wrong is still loading; anything else — a failure, or a body that
    // came back and turned out not to be a roster — is the console saying it cannot tell.
    const nothingHasHappenedYet = !input.view && !input.failed;
    return {
      state: nothingHasHappenedYet ? "loading" : "unavailable",
      rows: [],
      onlineCount: null,
      total: 0,
    };
  }

  // Online colleagues come first (#1795). The sort is stable, so each group keeps the server's
  // name order.
  const rows = members.map((member) => ({
    userId: member.user_id,
    label: memberLabel(member),
    email: member.email,
    role: member.role,
    state: (input.failed ? "unknown" : member.state) as DisplayPresenceState,
  })).sort((a, b) => Number(b.state === "online") - Number(a.state === "online"));

  return {
    // Unavailable outranks empty: "only your account so far" read off a stale answer is as much
    // an unverified claim as a colleague's state would be, and it is the more alarming one to get
    // wrong — it tells a business its staff have vanished.
    state: input.failed ? "unavailable" : rows.length === 0 ? "empty" : "ready",
    rows,
    // No count over unknowns: "3 online" printed above a list of question marks is a number the
    // console does not have.
    onlineCount: input.failed ? null : input.view.online_count,
    total: rows.length,
  };
}

/**
 * How long to wait before refreshing, in milliseconds.
 *
 * Taken from the server's own answer so the two cannot drift: a console beating slower than the
 * TTL would flicker its own user offline, and one beating far faster would just be load. The
 * fallback is only for the window before the first answer arrives.
 */
export const PRESENCE_FALLBACK_HEARTBEAT_MS = 30_000;

export function heartbeatIntervalMs(view: PresenceView | null): number {
  const seconds = view?.heartbeat_seconds;
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) {
    return PRESENCE_FALLBACK_HEARTBEAT_MS;
  }
  // Clamped: a server answer that said 0.2s or a day would otherwise become a busy loop or a
  // panel that never updates.
  return Math.min(300_000, Math.max(5_000, Math.round(seconds * 1000)));
}
